import type { InternalPageAdBanner } from './providers/internal-page-ad-banner'
import { BannerType, type BannerEventListenerArgs, type BannerEventType, type PageAdType } from './types'
import { AdConsoleLogger } from './utils/ad-console-logger'

export enum PublicPageAdBannerState {
    'loading' = 'loading',
    'empty' = 'empty',
    'showing' = 'showing',
}

export class PublicPageAdBanner extends EventTarget {
    readonly type = BannerType.IN_PAGE

    readonly placement: PageAdType

    readonly id: string

    state: PublicPageAdBannerState = PublicPageAdBannerState.loading

    #intermediateInternalBanner: InternalPageAdBanner | undefined
    #internalBanner: InternalPageAdBanner | undefined

    #containerEl: HTMLElement | undefined

    readonly #onRefresh: (() => void) | undefined

    #onDestroy: () => void

    readonly #adConsoleLogger: AdConsoleLogger

    constructor({
        placement,
        onDestroy,
        onRefresh,
    }: {
        placement: PageAdType
        onDestroy: () => void
        onRefresh?: () => void
    }) {
        super()

        this.placement = placement
        this.id = crypto.randomUUID()
        this.#onDestroy = onDestroy
        this.#onRefresh = onRefresh
        this.#adConsoleLogger = new AdConsoleLogger('public-banner', placement)
    }

    private dispatch<T>(event: BannerEventType, payload?: T) {
        this.dispatchEvent(new CustomEvent(event, { detail: payload }))
    }

    override addEventListener(...args: BannerEventListenerArgs) {
        super.addEventListener(...args)
    }

    override removeEventListener(...args: BannerEventListenerArgs) {
        super.removeEventListener(...args)
    }

    setIntermediateInternalBanner(banner: InternalPageAdBanner) {
        this.#intermediateInternalBanner = banner
    }

    setInternalBanner(banner: InternalPageAdBanner, container: HTMLElement) {
        this.#intermediateInternalBanner = undefined

        this.#internalBanner?.destroy()
        this.#internalBanner = banner

        this.#containerEl?.remove()
        this.#containerEl = container

        banner.addEventListener('viewable', () => this.triggerViewable())
        banner.addEventListener('request', () => this.triggerRequest())
    }

    get containerElId(): string | undefined {
        return this.#containerEl?.id
    }

    destroy() {
        this.#onDestroy()

        this.#intermediateInternalBanner?.destroy()
        this.#intermediateInternalBanner = undefined

        this.#internalBanner?.destroy()
        this.#internalBanner = undefined

        this.#containerEl?.remove()
        this.#containerEl = undefined
    }

    triggerEmpty(reason?: 'timeout' | 'unfilled' | 'no_container' | 'validation-error') {
        if (this.state !== PublicPageAdBannerState.empty) {
            this.#adConsoleLogger.debug(`${this.state} → empty`, { reason })
            this.state = PublicPageAdBannerState.empty
            this.dispatch('empty', { reason })
        }
    }

    triggerRequest() {
        this.dispatch('request')
    }

    triggerViewable() {
        this.#adConsoleLogger.debug('viewable')
        this.dispatch('viewable')
    }

    triggerRendered() {
        this.#adConsoleLogger.debug(`${this.state} → showing`)
        this.state = PublicPageAdBannerState.showing
        this.dispatch('rendered')
    }

    refresh() {
        if (this.state !== PublicPageAdBannerState.loading) {
            this.state = PublicPageAdBannerState.loading
            this.#onRefresh?.()
        }
    }
}
